// PricingCard · Lot M3
// Affichage cockpit des 4 valeurs pricing + badge M7 sur net vendeur.

import { Euro, Pencil, Lock, ArrowUpRight, ArrowDownRight, Sparkles } from "lucide-react";
import { fmtMoney, deltaPct } from "@/lib/money";

const Delta = ({ from, to }) => {
  const p = deltaPct(from, to);
  if (p == null || Math.abs(p) < 0.01) {
    return <span className="text-[11.5px] text-black/35">stable</span>;
  }
  const positive = p > 0;
  const Icon = positive ? ArrowUpRight : ArrowDownRight;
  return (
    <span className={`inline-flex items-center gap-1 text-[11.5px] ${positive ? "text-emerald-600" : "text-[#B91C1C]"}`}>
      <Icon size={11} />
      {positive ? "+" : ""}{p.toFixed(1)}%
    </span>
  );
};

const Row = ({ label, primary, secondary, tone = "normal", testId, children }) => (
  <div
    className="flex items-start justify-between gap-4 py-3 border-b border-black/[0.05] last:border-b-0"
    data-testid={testId}
  >
    <div>
      <div className="text-[10.5px] uppercase tracking-[0.14em] text-black/45">
        {label}
      </div>
      <div className={`mt-1 font-mono text-[20px] tracking-[-0.01em] ${
        tone === "muted" ? "text-black/60" : "text-black"
      }`}>
        {primary}
      </div>
      {secondary && (
        <div className="mt-0.5 text-[11.5px] text-black/40">{secondary}</div>
      )}
    </div>
    {children && <div className="flex-shrink-0 pt-2">{children}</div>}
  </div>
);


export default function PricingCard({
  pricing, initialized, isTerminal,
  onInitialize, onUpdateAsking,
}) {
  // Cas non initialisé — CTA d'initialisation.
  if (!initialized) {
    return (
      <section
        className="rounded-3xl bg-white/70 p-7"
        style={{ boxShadow: "inset 0 0 0 1px rgba(0,0,0,0.05)" }}
        data-testid="pricing-card-uninitialized"
      >
        <div className="flex items-start gap-3">
          <div className="mt-1 flex h-9 w-9 items-center justify-center rounded-xl bg-black/[0.04]">
            <Euro size={16} strokeWidth={1.7} className="text-black/60" />
          </div>
          <div className="flex-1">
            <h2 className="text-[16px] font-semibold tracking-[-0.01em]">
              Pricing
            </h2>
            <p className="mt-1 text-[12.5px] text-black/50 max-w-[440px]">
              Fixez le prix affiché et le net vendeur pour rendre le mandat
              prêt à commercialiser. Le net vendeur sera ensuite figé
              jusqu&apos;à validation d&apos;une proposition (M7).
            </p>
          </div>
          <span className="text-[10px] font-medium uppercase tracking-[0.14em] text-black/40">
            M3
          </span>
        </div>

        <div className="mt-6 flex items-center justify-between rounded-2xl bg-black/[0.03] px-5 py-4">
          <div className="text-[13px] text-black/55">
            Pricing non initialisé — le mandat ne peut pas passer à
            <b className="text-black"> Prêt à commercialiser</b>.
          </div>
          <button
            onClick={onInitialize}
            disabled={isTerminal}
            className="inline-flex flex-shrink-0 items-center gap-2 rounded-full bg-[#C8FF2F] px-4 py-2 text-[12.5px] font-medium text-black transition-transform hover:scale-[1.01] disabled:opacity-40"
            data-testid="pricing-initialize-btn"
          >
            <Sparkles size={13} /> Initialiser
          </button>
        </div>
      </section>
    );
  }

  const p = pricing;
  const cur = p.currency || "EUR";

  return (
    <section
      className="rounded-3xl bg-white/70 p-7"
      style={{ boxShadow: "inset 0 0 0 1px rgba(0,0,0,0.05)" }}
      data-testid="pricing-card"
    >
      <div className="flex items-start justify-between gap-4">
        <div className="flex items-start gap-3">
          <div className="mt-1 flex h-9 w-9 items-center justify-center rounded-xl bg-black/[0.04]">
            <Euro size={16} strokeWidth={1.7} className="text-black/60" />
          </div>
          <div>
            <h2 className="text-[16px] font-semibold tracking-[-0.01em]">
              Pricing
            </h2>
            <p className="mt-1 text-[12.5px] text-black/50">
              Structure économique du mandat · devise <b>{cur}</b>
            </p>
          </div>
        </div>
        <span className="text-[10px] font-medium uppercase tracking-[0.14em] text-black/40">
          M3
        </span>
      </div>

      <div className="mt-4 grid grid-cols-1 gap-x-8 md:grid-cols-2">
        {/* Colonne prix affiché */}
        <div>
          <div className="text-[10.5px] font-semibold uppercase tracking-[0.16em] text-black/60 pb-2 border-b border-black/[0.08]">
            Prix affiché
          </div>
          <Row
            label="Actuel"
            primary={fmtMoney(p.asking_price_current, cur)}
            secondary={
              <Delta from={p.asking_price_initial} to={p.asking_price_current} />
            }
            testId="pricing-asking-current-row"
          >
            <button
              onClick={onUpdateAsking}
              disabled={isTerminal}
              className="inline-flex items-center gap-1.5 rounded-full bg-white px-3 py-1.5 text-[12px] font-medium text-black/70 hover:text-black disabled:opacity-30"
              style={{ boxShadow: "inset 0 0 0 1px rgba(0,0,0,0.09)" }}
              data-testid="pricing-update-asking-btn"
            >
              <Pencil size={11} /> Modifier
            </button>
          </Row>
          <Row
            label="Initial (immuable)"
            primary={fmtMoney(p.asking_price_initial, cur)}
            tone="muted"
            testId="pricing-asking-initial-row"
          />
        </div>

        {/* Colonne net vendeur */}
        <div>
          <div className="text-[10.5px] font-semibold uppercase tracking-[0.16em] text-black/60 pb-2 border-b border-black/[0.08]">
            Net vendeur
          </div>
          <Row
            label="Actuel"
            primary={fmtMoney(p.net_seller_current, cur)}
            secondary={
              <span className="inline-flex items-center gap-1 text-[11px] uppercase tracking-[0.14em] text-black/45">
                <Lock size={10} /> Figé · modif. via proposition M7
              </span>
            }
            testId="pricing-net-current-row"
          />
          <Row
            label="Initial (immuable)"
            primary={fmtMoney(p.net_seller_initial, cur)}
            tone="muted"
            testId="pricing-net-initial-row"
          />
        </div>
      </div>
    </section>
  );
}
